import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, Typography,
  TableHead, TableRow, TableContainer, Button, CircularProgress, Alert,
} from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import type { Api, TenantView, PageForEdit, ReviewItem } from './api';

// Reviewer's queue: pages whose latest draft is awaiting review. Opening one
// takes the reviewer into the editor, where they Approve & publish or Request
// changes. Rendered inside the Layout content area.
export function Reviews({
  api, tenant, onOpen,
}: { api: Api; tenant: TenantView; onOpen: (p: PageForEdit) => void }) {
  const [items, setItems] = useState<ReviewItem[] | null>(null);
  const [err, setErr] = useState('');
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    api.reviewQueue(tenant.tenantId).then(setItems).catch((e) => setErr(e.message));
  }, [tenant.tenantId]);

  const open = async (id: string) => {
    setOpening(id);
    try { onOpen(await api.page(tenant.tenantId, id)); }
    catch (e: any) { setErr(e.message); }
    finally { setOpening(null); }
  };

  return (
    <Box sx={{ maxWidth: 900 }}>
        {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}
        {!items ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <RateReviewIcon sx={{ fontSize: 40, opacity: 0.4 }} />
            <Typography sx={{ mt: 1 }}>Nothing awaiting review.</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Page</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>/{it.slug}</TableCell>
                    <TableCell>{new Date(it.submittedAt).toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" disabled={opening === it.id} onClick={() => open(it.id)}>
                        {opening === it.id ? 'Opening…' : 'Review'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
    </Box>
  );
}
